import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ArrowLeft, BarChart3, Users, KeyRound, FileText, Activity, BookOpen, Search, Lock, FlaskConical, Cable} from 'lucide-react';
import logo from '@/assets/logo.svg';

interface DashboardSidebarProps {
  openSearchModal: () => void;
  onLinkClick?: () => void;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ openSearchModal, onLinkClick }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path);
  const takeToHome = () => {
    onLinkClick?.();
    if(location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }else{
      navigate('/');
    }
  };

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-dvh overflow-hidden dashboard-sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-2 cursor-pointer group" onClick={takeToHome}>
          <div className="w-6 h-6 rounded-md flex items-center justify-center relative transition-all duration-200 group-hover:scale-102 group-hover:translate-x-[-2px] group-hover:opacity-80">
            <img
              src={logo}
              alt="Logo"
              className="pointer-none select-none absolute inset-0 w-full h-full transition-opacity duration-200 opacity-100 group-hover:opacity-0"
              draggable="false"
            />
            <ArrowLeft
              className="w-4 h-4 text-black absolute inset-0 m-auto opacity-0 transition-opacity duration-200 group-hover:opacity-100"
              strokeWidth={2.5}
            />
          </div>
          <span className="font-semibold select-none text-black transition-all duration-200 group-hover:opacity-80">AXION</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
        <Link
          to="/dashboard"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard') && location.pathname === '/dashboard'
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <LayoutDashboard className="w-4 h-4" />
          <span>General</span>
        </Link>

       <Link
          to="/dashboard/playground"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard/playground')
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <FlaskConical className="w-4 h-4" />
          <span>Playground</span>
        </Link>


        <Link
          to="/dashboard/api-keys"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard/api-keys')
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <KeyRound className="w-4 h-4" />
          <span>API keys</span>
        </Link>

         <Link
          to="/dashboard/mcp"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard/mcp')
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <Cable className="w-4 h-4" />
          <span>MCP</span>
        </Link>
        <Link
          to="/dashboard/billing"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard/billing')
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>Billing</span>
        </Link>

        <Link
          to="/dashboard/usage"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard/usage')
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          <span>Usage</span>
        </Link>

        <Link
          to="/dashboard/service-health"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard/service-health')
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <Activity className="w-4 h-4" />
          <span>Service health</span>
        </Link>

        <Link
          to="/dashboard/security"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard/security')
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <Lock className="w-4 h-4" />
          <span>Security</span>
        </Link>

      </nav>

      {/* Bottom Section */}
      <div className="p-3 border-t border-gray-100 space-y-1 justify-self-end">
        <Link
          to="/dashboard/profile"
          onClick={onLinkClick}
          className="flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        >
          <Users className="w-4 h-4" />
          <span>Profile</span>
        </Link>

        <Link
          to="/docs"
          onClick={onLinkClick}
          className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
            isActive('/dashboard/docs')
              ? 'bg-gray-100 text-gray-900 font-medium'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          <span>Docs</span>
        </Link>
        <button
          className="flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-gray-600 hover:bg-gray-50 hover:text-gray-900 w-full cursor-pointer"
          onClick={() => { onLinkClick?.(); openSearchModal(); }}
        >
          <Search className="w-4 h-4" />
          <span>Search</span>
          <span className="ml-auto text-xs text-gray-400">⌘K</span>
        </button>
      </div>
    </aside>
  );
};

export default DashboardSidebar;
